import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Gruppe } from '../../../../../projects/kiene-core/src/lib/components/gruppen/classes/gruppe';
import { GruppeTier } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { StammdatenService } from '../../../../../projects/kiene-core/src/lib/components/stammdaten/stammdaten.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import {
    isFinishedOhrmarkePassBarcodeLength,
    isValidOhrmarkePassScanLength,
    normalizeOhrmarkePassScanInput,
    OHRMARKE_CHECK_DIGITS_SETTLE_MS,
    OHRMARKE_SCAN_LENGTH,
    OhrmarkeScanTrailGuard,
    PASS_SCAN_LENGTH,
    rawScanDigitCount,
} from '../../../../../projects/kiene-core/src/lib/utils/ohrmarke-pass-scan.util';
import {
    DashboardSammelstelleDialogData,
    DashboardSammelstelleDialogResult,
} from './dashboard-sammelstelle-dialog.data';
import {
    DashboardSammelstelleHerkunftPickDialogComponent,
    DashboardSammelstelleHerkunftPickResult,
} from './dashboard-sammelstelle-herkunft-pick-dialog.component';
import {
    DashboardSammelstelleOptionPickDialogComponent,
    DashboardSammelstelleOptionPickResult,
    SammelPickOption,
} from './dashboard-sammelstelle-option-pick-dialog.component';
import { isSammelstelleBetriebsart } from './dashboard-sammelstelle-access';
import { DashboardSammelstelleSaveService } from './dashboard-sammelstelle-save.service';
import {
    emptySammelnTier,
    formatOhrmarkeDisplay,
    herkunftFieldsFromBetriebskontakt,
    takeShortcuts,
    tippeGewicht,
    type SammelnGeschlecht,
    type SammelnHerkunftOption,
    type SammelnTier,
} from './dashboard-sammelstelle.types';

function recordsOf(body: unknown): any[] {
    if (Array.isArray(body)) return body;
    const records = (body as { records?: unknown })?.records;
    return Array.isArray(records) ? records : [];
}

function uniqueLabels(values: unknown[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of values) {
        const t = String(raw ?? '').trim();
        if (!t || seen.has(t)) continue;
        seen.add(t);
        out.push(t);
    }
    return out;
}

@Component({
    selector: 'kiene-dashboard-sammelstelle-dialog',
    templateUrl: './dashboard-sammelstelle-dialog.component.html',
    styleUrls: ['./dashboard-sammelstelle-dialog.component.scss'],
    providers: [DashboardSammelstelleSaveService],
})
export class DashboardSammelstelleDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('scanInput') scanInput?: ElementRef<HTMLInputElement>;

    scanText = '';
    scanHinweis = '';
    /**
     * Einstalldatum shown in UI.
     * Existing animal → loaded from DB. New animal → empty until user sets it
     * (save falls back to today for create). Never auto-fill "today" into the display.
     */
    datum: Date | null = null;
    einstalldatumLocked = true;
    private einstalldatumManuallyEdited = false;
    aktuell: SammelnTier | null = null;
    geprueft: SammelnTier[] = [];
    /** Unknown scans queued for Einstall handoff (no create inside Sammelstelle). */
    nichtGefunden: SammelnTier[] = [];
    letztesAbteil: string | null = null;
    /** Last non-empty Bemerkung selection — preselected on next unknown scan. */
    letzteBemerkungen: string[] = [];
    /** Last non-empty Reklamation selection — preselected on next unknown scan. */
    letzteReklamationen: string[] = [];
    letzteReklamationIds: number[] = [];
    passHerkunft: SammelnHerkunftOption | null = null;
    zielGruppe: Gruppe | null = null;
    gruppen: Gruppe[] = [];
    tierlisteId: number | null = null;
    saving = false;
    loadingOptions = false;

    abteilOptionen: string[] = [];
    haendlerOptionen: Array<{ label: string; id?: number }> = [];
    herkunftOptionen: SammelnHerkunftOption[] = [];
    bemerkungOptionen: string[] = ['Nabel', 'Durchfall', 'Kümmerer', 'Lahm', 'Sauber'];
    reklamationOptionen: Array<{ label: string; id?: number }> = [];
    /** Desktop landscape shortcut chips (mobile parity). */
    readonly shortcutLimit = 5;

    private readonly destroy$ = new Subject<void>();
    private readonly trailGuard = new OhrmarkeScanTrailGuard();
    private autoScanSettleTimer: ReturnType<typeof setTimeout> | null = null;
    private scanSeq = 0;

    constructor(
        private readonly dialogRef: MatDialogRef<
            DashboardSammelstelleDialogComponent,
            DashboardSammelstelleDialogResult
        >,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: DashboardSammelstelleDialogData | null,
        private readonly localStorageService: LocalStorageService,
        private readonly api: KieneBackendApiService,
        private readonly stammdatenService: StammdatenService,
        private readonly saveService: DashboardSammelstelleSaveService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    get roleAllowed(): boolean {
        return this.localStorageService.canUseSammelstelle();
    }

    get betriebAllowed(): boolean {
        const fromData = this.data?.client?.betriebsart_id;
        const fromSession = this.localStorageService.getCurrentClient()?.betriebsart_id;
        return isSammelstelleBetriebsart(fromData ?? fromSession);
    }

    get allowed(): boolean {
        return this.roleAllowed && this.betriebAllowed;
    }

    get kundeId(): number | null {
        const fromData = Number(this.data?.client?.kunde_id ?? 0);
        if (fromData > 0) return fromData;
        const current = Number(this.localStorageService.getCurrentClient()?.kunde_id ?? 0);
        return current > 0 ? current : null;
    }

    get ohrmarkeDisplay(): string {
        return this.aktuell ? formatOhrmarkeDisplay(this.aktuell.ohrmarke) : '—';
    }

    get einstalldatumDisplay(): string {
        if (!(this.datum instanceof Date) || Number.isNaN(this.datum.getTime())) {
            return '—';
        }
        const dd = String(this.datum.getDate()).padStart(2, '0');
        const mm = String(this.datum.getMonth() + 1).padStart(2, '0');
        const yyyy = this.datum.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    private static todayDate(): Date {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    /** First parseable API date (skips null/empty/zero dates). */
    private static firstApiDate(...values: unknown[]): Date | null {
        for (const value of values) {
            const parsed = DashboardSammelstelleDialogComponent.parseApiDate(value);
            if (parsed) {
                return parsed;
            }
        }
        return null;
    }

    private static parseApiDate(value: unknown): Date | null {
        if (value == null || value === '') return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            if (value.getFullYear() < 1900) return null;
            const d = new Date(value.getTime());
            d.setHours(0, 0, 0, 0);
            return d;
        }
        const raw = String(value).trim();
        if (!raw || raw === 'null' || raw === 'undefined') return null;
        // yyyy-MM-dd or ISO
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) {
            const year = Number(iso[1]);
            const month = Number(iso[2]);
            const day = Number(iso[3]);
            if (year < 1900 || month < 1 || day < 1) return null;
            const d = new Date(year, month - 1, day);
            d.setHours(0, 0, 0, 0);
            return Number.isNaN(d.getTime()) ? null : d;
        }
        // dd.MM.yyyy
        const de = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
        if (de) {
            const d = new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]));
            d.setHours(0, 0, 0, 0);
            if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return null;
            return d;
        }
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() < 1900) return null;
        parsed.setHours(0, 0, 0, 0);
        return parsed;
    }

    get abteilShortcuts(): string[] {
        return takeShortcuts(this.abteilOptionen, this.aktuell?.abteil ?? null, this.shortcutLimit);
    }

    get haendlerShortcuts(): string[] {
        return takeShortcuts(
            this.haendlerOptionen.map(o => o.label),
            this.aktuell?.haendler ?? null,
            this.shortcutLimit,
        );
    }

    get herkunftAnzeige(): string[] {
        const current = this.aktuell?.herkunft ?? this.passHerkunft?.label ?? null;
        const labels = this.herkunftOptionen.map(o => o.label);
        if (current && !labels.includes(current)) {
            return takeShortcuts([current, ...labels], current, Math.min(3, this.shortcutLimit));
        }
        return takeShortcuts(labels, current, Math.min(3, this.shortcutLimit));
    }

    get bemerkungShortcuts(): string[] {
        return takeShortcuts(
            this.bemerkungOptionen,
            this.aktuell?.bemerkungen ?? [],
            this.shortcutLimit,
        );
    }

    get reklamationShortcuts(): string[] {
        return takeShortcuts(
            this.reklamationOptionen.map(o => o.label),
            this.aktuell?.reklamationen ?? [],
            this.shortcutLimit,
        );
    }

    ngOnInit(): void {
        if (!this.allowed) {
            return;
        }
        this.loadGruppen();
        this.loadOptions();
        const preloaded = this.data?.gruppe ?? null;
        if (preloaded && Number(preloaded.gruppe_id) > 0) {
            this.zielGruppe = preloaded;
            this.einstalldatumLocked = true;
            this.loadOptions();
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.focusScan(), 0);
    }

    ngOnDestroy(): void {
        if (this.autoScanSettleTimer != null) {
            clearTimeout(this.autoScanSettleTimer);
            this.autoScanSettleTimer = null;
        }
        this.trailGuard.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }

    close(): void {
        this.dialogRef.close(this.geprueft.some(t => t.saved));
    }

    get canOpenEinstallen(): boolean {
        return this.nichtGefundenHandoffCount > 0;
    }

    get nichtGefundenHandoffCount(): number {
        return this.collectNichtGefundenDrafts().length;
    }

    /** Handoff Nicht gefunden → existing Einstall stepper (prefilled). Zielgruppe optional. */
    openEinstallen(): void {
        const prefillTiere = this.collectNichtGefundenDrafts();
        if (!prefillTiere.length || this.saving) {
            return;
        }
        const kp =
            this.datum instanceof Date && !Number.isNaN(this.datum.getTime())
                ? formatDate(this.datum, 'yyyy-MM-dd', 'de')
                : null;
        const gruppeId =
            Number(this.zielGruppe?.gruppe_id) > 0 ? Number(this.zielGruppe.gruppe_id) : null;
        const kundeId = this.kundeId;

        // Open Einstall from here (not via destroyed parent afterClosed) so the queue is never lost.
        void this.openEinstallStepperWithPrefill({
            prefillTiere,
            gruppeId,
            kundeId,
            kp_einstalldatum: kp,
        }).then(() => {
            this.dialogRef.close(this.geprueft.some(t => t.saved));
        });
    }

    private async openEinstallStepperWithPrefill(opts: {
        prefillTiere: Array<{
            ohrmarke: string;
            geschlecht?: 'm' | 'w' | null;
            gewicht?: string;
            haendler?: string | null;
            haendlerId?: number | null;
            herkunft?: string | null;
            herkunftKundeId?: number | null;
            herkunftFields?: SammelnTier['herkunftFields'];
            abteil?: string | null;
            bemerkungen?: string[];
            reklamationen?: string[];
            reklamationIds?: number[];
        }>;
        gruppeId?: number | null;
        kundeId?: number | null;
        kp_einstalldatum?: string | null;
    }): Promise<void> {
        const [{ DashboardEinstallenStepperDialogComponent }, dataMod] = await Promise.all([
            import('../dashboard-einstallen-stepper-dialog/dashboard-einstallen-stepper-dialog.component'),
            import('../dashboard-einstallen-stepper-dialog/dashboard-einstallen-stepper-dialog.data'),
        ]);
        const gruppeId = Number(opts.gruppeId || 0);
        const prefillOhrmarken = opts.prefillTiere.map(t => t.ohrmarke);
        const stepperData =
            gruppeId > 0
                ? {
                      continueDirectScan: {
                          gruppeId,
                          kundeId: opts.kundeId ?? null,
                          // Land on Einstallen overview so Ohrmarken are visible (not only Prüfen counts).
                          initialStep: 'einstallen' as const,
                          prefillTiere: opts.prefillTiere,
                          prefillOhrmarken,
                          kp_einstalldatum: opts.kp_einstalldatum ?? null,
                      },
                  }
                : {
                      sammelstellePrefill: {
                          prefillTiere: opts.prefillTiere,
                          prefillOhrmarken,
                          kundeId: opts.kundeId ?? null,
                          kp_einstalldatum: opts.kp_einstalldatum ?? null,
                      },
                  };
        this.dialog.open(DashboardEinstallenStepperDialogComponent, {
            ...dataMod.dashboardEinstallenStepperDialogLayout,
            data: stepperData,
        });
    }

    /** Full drafts for Einstall handoff (Ohrmarke + Gewicht/Abteil/… stay in memory until create). */
    private collectNichtGefundenDrafts(): Array<{
        ohrmarke: string;
        geschlecht: SammelnGeschlecht | null;
        gewicht: string;
        haendler: string | null;
        haendlerId: number | null;
        herkunft: string | null;
        herkunftKundeId: number | null;
        herkunftFields: SammelnTier['herkunftFields'];
        abteil: string | null;
        bemerkungen: string[];
        reklamationen: string[];
        reklamationIds: number[];
    }> {
        const seen = new Set<string>();
        const out: ReturnType<DashboardSammelstelleDialogComponent['collectNichtGefundenDrafts']> = [];
        const push = (tier: SammelnTier) => {
            const digits = normalizeOhrmarkePassScanInput(tier.ohrmarke);
            if (!isValidOhrmarkePassScanLength(digits) || seen.has(digits)) {
                return;
            }
            seen.add(digits);
            out.push({
                ohrmarke: digits,
                geschlecht: tier.geschlecht ?? null,
                gewicht: String(tier.gewicht ?? ''),
                haendler: tier.haendler ?? null,
                haendlerId: tier.haendlerId ?? null,
                herkunft: tier.herkunft ?? null,
                herkunftKundeId: tier.herkunftKundeId ?? null,
                herkunftFields: tier.herkunftFields ?? null,
                abteil: tier.abteil ?? null,
                bemerkungen: [...(tier.bemerkungen ?? [])],
                reklamationen: [...(tier.reklamationen ?? [])],
                reklamationIds: [...(tier.reklamationIds ?? [])],
            });
        };
        for (const t of this.nichtGefunden) {
            push(t);
        }
        if (this.aktuell && !(Number(this.aktuell.gtId ?? 0) > 0)) {
            push(this.aktuell);
        }
        return out;
    }

    private pushNichtGefunden(tier: SammelnTier): void {
        const digits = normalizeOhrmarkePassScanInput(tier.ohrmarke);
        this.nichtGefunden = [
            { ...tier, ohrmarke: digits, gtId: null, saved: false },
            ...this.nichtGefunden.filter(
                t => normalizeOhrmarkePassScanInput(t.ohrmarke) !== digits,
            ),
        ];
    }

    removeNichtGefunden(ohrmarke: string): void {
        const digits = normalizeOhrmarkePassScanInput(ohrmarke);
        this.nichtGefunden = this.nichtGefunden.filter(
            t => normalizeOhrmarkePassScanInput(t.ohrmarke) !== digits,
        );
    }

    focusScan(): void {
        this.scanInput?.nativeElement?.focus();
    }

    /** Clear scan field and refocus after Geprüft (button click steals focus). */
    private readyForNextScan(): void {
        this.scanText = '';
        this.scanHinweis = '';
        this.trailGuard.armTrailingIgnore();
        setTimeout(() => {
            const el = this.scanInput?.nativeElement;
            if (!el) return;
            el.focus();
            el.select?.();
        }, 0);
    }

    onScanInput(value: string): void {
        if (this.trailGuard.shouldIgnoreIncoming()) {
            this.scanText = '';
            return;
        }
        this.scanText = value;
        this.scanHinweis = '';
        this.trailGuard.cancelSettle();
        if (this.autoScanSettleTimer != null) {
            clearTimeout(this.autoScanSettleTimer);
            this.autoScanSettleTimer = null;
        }

        const digitCount = rawScanDigitCount(value);
        // Prefer full 12-digit ear-tag (10+check) or 15-digit pass before accepting.
        if (isFinishedOhrmarkePassBarcodeLength(digitCount)) {
            this.applyScan(value);
            return;
        }
        // Exactly 10: wait briefly — check digits from the same scan may still arrive.
        if (digitCount === OHRMARKE_SCAN_LENGTH) {
            this.autoScanSettleTimer = setTimeout(() => {
                this.autoScanSettleTimer = null;
                this.applyScan(this.scanText);
            }, OHRMARKE_CHECK_DIGITS_SETTLE_MS);
        }
        // 1–9 / 11–14: keep waiting for more digits (scanner still typing).
    }

    onScanEnter(event?: Event): void {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (this.autoScanSettleTimer != null) {
            clearTimeout(this.autoScanSettleTimer);
            this.autoScanSettleTimer = null;
        }
        // Scanner suffix Enter often arrives after a successful 12-digit accept cleared the field.
        if (!rawScanDigitCount(this.scanText)) {
            return;
        }
        this.trailGuard.settleThenRun(
            () => this.scanText,
            () => this.applyScan(this.scanText, { fromEnter: true }),
        );
    }

    applyScan(rawInput: string, options?: { fromEnter?: boolean }): void {
        if (this.autoScanSettleTimer != null) {
            clearTimeout(this.autoScanSettleTimer);
            this.autoScanSettleTimer = null;
        }
        this.trailGuard.cancelSettle();

        const digitCount = rawScanDigitCount(rawInput);
        // Empty: ignore (typical leftover Enter after a successful scan cleared the field).
        if (!digitCount) {
            return;
        }

        const raw = normalizeOhrmarkePassScanInput(rawInput);
        if (!isValidOhrmarkePassScanLength(raw)) {
            // Incomplete frame (e.g. 11 of 12 still arriving) — don't flash an error unless Enter.
            if (!options?.fromEnter && digitCount < PASS_SCAN_LENGTH) {
                return;
            }
            this.scanHinweis = 'Ohrmarke mit 10 Stellen oder Pass mit 15 Stellen scannen.';
            this.focusScan();
            return;
        }
        const seq = ++this.scanSeq;
        this.aktuell = emptySammelnTier(raw, {
            abteil: this.letztesAbteil,
            bemerkungen: this.letzteBemerkungen,
            reklamationen: this.letzteReklamationen,
            reklamationIds: this.letzteReklamationIds,
        });
        this.passHerkunft = null;
        this.scanText = '';
        this.scanHinweis = '';
        this.trailGuard.armTrailingIgnore();
        // Keep previous datum visible until lookup resolves (avoids empty flash).
        this.einstalldatumLocked = true;
        this.focusScan();

        this.saveService
            .lookupExisting({
                ohrmarke: raw,
                kundeId: this.kundeId,
                gruppeId: this.zielGruppe?.gruppe_id,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: existing => {
                    if (seq !== this.scanSeq) return;
                    if (!existing) {
                        // New animal: keep Einstalldatum empty in UI (save uses today).
                        if (!this.einstalldatumManuallyEdited) {
                            this.datum = null;
                        }
                        return;
                    }
                    this.applyExistingTier(existing, raw);
                },
            });
    }

    /** Hydrate scan UI from existing animal and merge values into chip options. */
    private applyExistingTier(existing: GruppeTier | any, ohrmarke: string): void {
        const hydrated = this.saveService.fromExisting(existing, {
            ohrmarke,
            letztesAbteil: this.letztesAbteil,
        });
        this.aktuell = hydrated;
        this.passHerkunft = this.saveService.herkunftFromTier(existing);

        // Always show the animal's real Einstalldatum (never today's date).
        // Prefer coalesce field first; try each value (empty kp must not block notnull/hit).
        if (!this.einstalldatumManuallyEdited) {
            this.datum = DashboardSammelstelleDialogComponent.firstApiDate(
                existing?.einstalldatum_notnull,
                existing?.kp_einstalldatum,
                existing?.hit_einstalldatum,
                existing?.einstalldatum,
            );
        }
        this.einstalldatumLocked = true;

        if (hydrated.abteil && !this.abteilOptionen.includes(hydrated.abteil)) {
            this.abteilOptionen = [hydrated.abteil, ...this.abteilOptionen];
        }
        if (hydrated.haendler) {
            const has = this.haendlerOptionen.some(o => o.label === hydrated.haendler);
            if (!has) {
                this.haendlerOptionen = [
                    {
                        label: hydrated.haendler,
                        id: hydrated.haendlerId ?? undefined,
                    },
                    ...this.haendlerOptionen,
                ];
            } else if (hydrated.haendlerId && hydrated.haendlerId > 0) {
                const row = this.haendlerOptionen.find(o => o.label === hydrated.haendler);
                if (row && !(row.id > 0)) row.id = hydrated.haendlerId;
            }
        }
        if (this.passHerkunft?.label) {
            const has = this.herkunftOptionen.some(o => o.label === this.passHerkunft.label);
            if (!has) {
                this.herkunftOptionen = [this.passHerkunft, ...this.herkunftOptionen];
            }
        }
        for (const b of hydrated.bemerkungen) {
            if (!this.bemerkungOptionen.includes(b)) {
                this.bemerkungOptionen = [b, ...this.bemerkungOptionen];
            }
        }
        for (const r of existing?.reklamationen ?? []) {
            const label = String(
                (r as { reklamation?: string; bezeichnung?: string })?.reklamation ??
                    (r as { bezeichnung?: string })?.bezeichnung ??
                    '',
            ).trim();
            const id = Number((r as { tierreklamation_id?: number })?.tierreklamation_id ?? 0);
            if (!label) continue;
            const existingOpt = this.reklamationOptionen.find(o => o.label === label);
            if (!existingOpt) {
                this.reklamationOptionen = [
                    { label, id: id > 0 ? id : undefined },
                    ...this.reklamationOptionen,
                ];
            } else if (id > 0 && !(existingOpt.id > 0)) {
                existingOpt.id = id;
            }
        }
        // Prefer animal group as Zielgruppe when none chosen yet.
        const gid = Number(hydrated.sourceGruppeId ?? existing?.gruppe_id ?? 0);
        if (gid > 0 && !(Number(this.zielGruppe?.gruppe_id) > 0)) {
            const match = this.gruppen.find(g => Number(g.gruppe_id) === gid) ?? null;
            if (match) {
                this.zielGruppe = match;
                this.einstalldatumLocked = true;
                this.loadOptions();
            }
        }
    }

    setGeschlecht(g: SammelnGeschlecht): void {
        if (!this.aktuell) return;
        this.aktuell = {
            ...this.aktuell,
            geschlecht: this.aktuell.geschlecht === g ? null : g,
        };
    }

    tippeGewichtKey(key: string): void {
        if (!this.aktuell) return;
        this.aktuell = {
            ...this.aktuell,
            gewicht: tippeGewicht(this.aktuell.gewicht, key),
        };
    }

    toggleChip(field: 'abteil' | 'haendler' | 'herkunft', value: string): void {
        if (!this.aktuell) return;
        if (field === 'abteil') {
            const next = this.aktuell.abteil === value ? null : value;
            this.aktuell = { ...this.aktuell, abteil: next };
            if (next) this.rememberLastShortcuts(this.aktuell);
            return;
        }
        if (field === 'haendler') {
            const next = this.aktuell.haendler === value ? null : value;
            const id = this.haendlerOptionen.find(o => o.label === value)?.id ?? null;
            this.aktuell = {
                ...this.aktuell,
                haendler: next,
                haendlerId: next ? id : null,
            };
            return;
        }
        if (this.aktuell.herkunft === value) {
            this.aktuell = {
                ...this.aktuell,
                herkunft: null,
                herkunftKundeId: null,
                herkunftFields: null,
            };
            return;
        }
        const opt =
            this.herkunftOptionen.find(o => o.label === value) ||
            (this.passHerkunft?.label === value ? this.passHerkunft : null);
        if (opt) {
            this.aktuell = {
                ...this.aktuell,
                herkunft: opt.label,
                herkunftKundeId: opt.herkunftKundeId ?? null,
                herkunftFields: opt.fields,
            };
            return;
        }
        this.aktuell = {
            ...this.aktuell,
            herkunft: value,
            herkunftKundeId: null,
            herkunftFields: {
                herkunft_vvvo: '',
                herkunft_betrieb: value,
                herkunft_strasse: '',
                herkunft_plz: '',
                herkunft_ort: '',
                herkunft_ortsteil: '',
            },
        };
    }

    toggleMulti(field: 'bemerkungen' | 'reklamationen', value: string): void {
        if (!this.aktuell) return;
        const list = [...this.aktuell[field]];
        const idx = list.indexOf(value);
        if (idx >= 0) list.splice(idx, 1);
        else list.push(value);
        if (field === 'bemerkungen') {
            this.aktuell = { ...this.aktuell, bemerkungen: list };
            this.rememberLastShortcuts({ ...this.aktuell, bemerkungen: list });
            return;
        }
        const ids = list
            .map(label => this.reklamationOptionen.find(o => o.label === label)?.id ?? 0)
            .filter(id => id > 0);
        this.aktuell = { ...this.aktuell, reklamationen: list, reklamationIds: ids };
        this.rememberLastShortcuts(this.aktuell);
    }

    /** Remember last Abteil / Bemerkung / Reklamation for the next unknown animal. */
    private rememberLastShortcuts(tier: Pick<
        SammelnTier,
        'abteil' | 'bemerkungen' | 'reklamationen' | 'reklamationIds'
    >): void {
        if (tier.abteil?.trim()) {
            this.letztesAbteil = tier.abteil.trim();
        }
        if (tier.bemerkungen?.length) {
            this.letzteBemerkungen = [...tier.bemerkungen];
        }
        if (tier.reklamationen?.length) {
            this.letzteReklamationen = [...tier.reklamationen];
            this.letzteReklamationIds = [...(tier.reklamationIds ?? [])].filter(id => id > 0);
        }
    }

    onZielgruppeChange(gruppeId: number | string): void {
        const id = Number(gruppeId);
        this.zielGruppe = this.gruppen.find(g => Number(g.gruppe_id) === id) ?? null;
        this.tierlisteId = null;
        this.einstalldatumLocked = true;
        this.loadOptions();
    }

    unlockEinstalldatum(): void {
        this.einstalldatumLocked = false;
        this.einstalldatumManuallyEdited = true;
        // Seed picker with today only as a starting point for the editor, not as loaded data.
        if (!(this.datum instanceof Date) || Number.isNaN(this.datum.getTime())) {
            this.datum = DashboardSammelstelleDialogComponent.todayDate();
        }
    }

    onEinstalldatumModelChange(value: Date | null): void {
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            value.setHours(0, 0, 0, 0);
            this.datum = value;
            this.einstalldatumManuallyEdited = true;
            return;
        }
        this.datum = null;
        this.einstalldatumManuallyEdited = true;
    }

    onEinstalldatumPickerClosed(): void {
        this.einstalldatumLocked = true;
    }

    /** Date string for create payload – user value, else today for new animals. */
    private resolveEinstalldatumForSave(): string {
        const d =
            this.datum instanceof Date && !Number.isNaN(this.datum.getTime())
                ? this.datum
                : DashboardSammelstelleDialogComponent.todayDate();
        return formatDate(d, 'yyyy-MM-dd', 'de');
    }

    openMoreAbteil(): void {
        this.openOptionPick({
            title: 'Abteil wählen',
            options: this.abteilOptionen.map(label => ({ label })),
            selected: this.aktuell?.abteil ?? null,
            createLabel: 'Neues Abteil',
            onResult: result => {
                const label = result.labels[0];
                if (!label || !this.aktuell) return;
                if (result.created && !this.abteilOptionen.includes(label)) {
                    this.abteilOptionen = [label, ...this.abteilOptionen];
                }
                this.aktuell = { ...this.aktuell, abteil: label };
                this.letztesAbteil = label;
                this.rememberLastShortcuts(this.aktuell);
            },
        });
    }

    openMoreHaendler(): void {
        this.openOptionPick({
            title: 'Händler wählen',
            options: this.haendlerOptionen,
            selected: this.aktuell?.haendler ?? null,
            onResult: result => {
                const label = result.labels[0];
                if (!label || !this.aktuell) return;
                const id = this.haendlerOptionen.find(o => o.label === label)?.id ?? null;
                this.aktuell = { ...this.aktuell, haendler: label, haendlerId: id };
            },
        });
    }

    openMoreHerkunft(): void {
        this.dialog
            .open(DashboardSammelstelleHerkunftPickDialogComponent, {
                width: '720px',
                maxWidth: '96vw',
                data: { selectedKundeId: this.aktuell?.herkunftKundeId ?? null },
                autoFocus: false,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: DashboardSammelstelleHerkunftPickResult | null) => {
                if (!result || !this.aktuell) return;
                if (result.clear) {
                    this.aktuell = {
                        ...this.aktuell,
                        herkunft: null,
                        herkunftKundeId: null,
                        herkunftFields: null,
                    };
                    this.focusScan();
                    return;
                }
                const kontakt = result.kontakt;
                if (!kontakt) return;
                const fields = herkunftFieldsFromBetriebskontakt(kontakt);
                const label =
                    String(fields.herkunft_betrieb || '').trim() ||
                    String(fields.herkunft_vvvo || '').trim() ||
                    'Herkunft';
                const opt: SammelnHerkunftOption = {
                    label,
                    herkunftKundeId: Number(kontakt.kunde_id) > 0 ? Number(kontakt.kunde_id) : null,
                    fields,
                };
                if (!this.herkunftOptionen.some(o => o.label === opt.label)) {
                    this.herkunftOptionen = [opt, ...this.herkunftOptionen];
                }
                this.aktuell = {
                    ...this.aktuell,
                    herkunft: opt.label,
                    herkunftKundeId: opt.herkunftKundeId ?? null,
                    herkunftFields: opt.fields,
                };
                this.focusScan();
            });
    }

    openMoreBemerkung(): void {
        this.openOptionPick({
            title: 'Bemerkung wählen',
            options: this.bemerkungOptionen.map(label => ({ label })),
            selected: this.aktuell?.bemerkungen ?? [],
            multi: true,
            createLabel: 'Neue Bemerkung',
            onResult: result => {
                if (!this.aktuell) return;
                if (result.created && !this.bemerkungOptionen.includes(result.created)) {
                    this.bemerkungOptionen = [result.created, ...this.bemerkungOptionen];
                }
                this.aktuell = { ...this.aktuell, bemerkungen: result.labels };
                this.rememberLastShortcuts(this.aktuell);
            },
        });
    }

    openMoreReklamation(): void {
        this.openOptionPick({
            title: 'Reklamation wählen',
            options: this.reklamationOptionen,
            selected: this.aktuell?.reklamationen ?? [],
            multi: true,
            createLabel: 'Neue Reklamation',
            onResult: result => {
                if (!this.aktuell) return;
                const applyLabels = (labels: string[], createdId?: number) => {
                    if (!this.aktuell) return;
                    if (result.created) {
                        const existing = this.reklamationOptionen.find(o => o.label === result.created);
                        if (!existing) {
                            this.reklamationOptionen = [
                                {
                                    label: result.created,
                                    id: createdId && createdId > 0 ? createdId : undefined,
                                },
                                ...this.reklamationOptionen,
                            ];
                        } else if (createdId && createdId > 0 && !(existing.id > 0)) {
                            existing.id = createdId;
                            this.reklamationOptionen = [...this.reklamationOptionen];
                        }
                    }
                    const ids = labels
                        .map(label => this.reklamationOptionen.find(o => o.label === label)?.id ?? 0)
                        .filter(id => id > 0);
                    this.aktuell = {
                        ...this.aktuell,
                        reklamationen: labels,
                        reklamationIds: ids,
                    };
                    this.rememberLastShortcuts(this.aktuell);
                };

                // Mobile parity: free-text Reklamation must be created in catalog to get an id,
                // otherwise updateReklamationenMultiple skips it.
                const created = String(result.created ?? '').trim();
                if (created && !(this.reklamationOptionen.find(o => o.label === created)?.id > 0)) {
                    this.api
                        .post(
                            this.apiBaseUrl + 'tierreklamation/update_kundenportal.php',
                            { reklamation: created },
                            undefined,
                        )
                        .subscribe({
                            next: body => {
                                const id = Number(
                                    (body as { tierreklamation_id?: number })?.tierreklamation_id ?? 0,
                                );
                                applyLabels(result.labels, id);
                            },
                            error: err => {
                                applyLabels(result.labels);
                                this.messageService.errorMessage(
                                    'Neue Reklamation konnte nicht im Katalog angelegt werden.',
                                    err,
                                );
                            },
                        });
                    return;
                }
                applyLabels(result.labels);
            },
        });
    }

    private openOptionPick(opts: {
        title: string;
        options: SammelPickOption[];
        selected: string | null | string[];
        multi?: boolean;
        createLabel?: string;
        onResult: (result: DashboardSammelstelleOptionPickResult) => void;
    }): void {
        this.dialog
            .open(DashboardSammelstelleOptionPickDialogComponent, {
                width: '480px',
                maxWidth: '92vw',
                autoFocus: false,
                panelClass: 'dashboard-sammelstelle-option-pick-panel',
                data: {
                    title: opts.title,
                    options: opts.options,
                    selected: opts.selected,
                    multi: opts.multi,
                    createLabel: opts.createLabel,
                },
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: DashboardSammelstelleOptionPickResult | null) => {
                if (!result) return;
                opts.onResult(result);
                this.focusScan();
            });
    }

    uebernehmen(): void {
        if (!this.aktuell || this.saving) return;
        const prepared = { ...this.aktuell };
        this.rememberLastShortcuts(prepared);
        const existingGtId = Number(prepared.gtId ?? 0);

        // Unknown animals stay in Sammelstelle as Nicht gefunden — Einstall creates them later.
        if (!(existingGtId > 0)) {
            this.pushNichtGefunden(prepared);
            this.aktuell = null;
            this.passHerkunft = null;
            if (!this.einstalldatumManuallyEdited) {
                this.datum = null;
            }
            this.readyForNextScan();
            return;
        }

        this.saving = true;
        this.saveService
            .save({
                tier: prepared,
                gruppeId: this.zielGruppe?.gruppe_id,
                kundeId: this.kundeId,
                tierlisteId: this.tierlisteId,
                datum: this.resolveEinstalldatumForSave(),
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: result => {
                    this.saving = false;
                    if (result.tierlisteId != null && result.tierlisteId > 0) {
                        this.tierlisteId = result.tierlisteId;
                    }
                    this.geprueft = [result.tier, ...this.geprueft];
                    this.aktuell = null;
                    this.passHerkunft = null;
                    this.readyForNextScan();
                },
                error: err => {
                    this.saving = false;
                    this.messageService.errorMessage('Speichern fehlgeschlagen', err);
                },
            });
    }

    private loadGruppen(): void {
        const kid = this.kundeId;
        if (!(kid > 0)) {
            this.gruppen = [];
            return;
        }
        let params = new HttpParams().set('kunde_id', String(kid));
        this.api
            .getAll(this.apiBaseUrl + 'gruppe/read_akt_kundenportal.php', params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: body => {
                    this.gruppen = recordsOf(body) as Gruppe[];
                    const preferredId =
                        Number(this.zielGruppe?.gruppe_id ?? 0) ||
                        Number(this.data?.gruppe?.gruppe_id ?? 0);
                    if (preferredId > 0) {
                        const match = this.gruppen.find(g => Number(g.gruppe_id) === preferredId);
                        if (match) {
                            this.zielGruppe = match;
                            this.einstalldatumLocked = true;
                        }
                    }
                },
                error: () => {
                    this.gruppen = [];
                },
            });
    }

    private loadOptions(): void {
        const kid = this.kundeId;
        if (!(kid > 0)) return;
        this.loadingOptions = true;
        const gid = Number(this.zielGruppe?.gruppe_id ?? 0);

        const abteileKunde$ = this.api.getAll(
            this.apiBaseUrl + 'gruppe_tier/read_kunde_abteil.php',
            new HttpParams().set('kunde_id', String(kid)),
        );
        const abteileGruppe$ =
            gid > 0 ? this.stammdatenService.ladeAbteile(gid) : of({ records: [] });
        const herkunft$ =
            gid > 0 ? this.stammdatenService.ladeHerkunftsbetriebe(gid) : of({ records: [] });
        const reklGruppe$ =
            gid > 0
                ? this.api.getAll(
                      this.apiBaseUrl + 'gruppe_tier/read_gruppe_reklamation.php',
                      new HttpParams().set('gruppe_id', String(gid)),
                  )
                : of({ records: [] });
        // Same portal endpoint as mobile listViehhaendlerAll / StammdatenService.loadHaendler
        const haendler$ = this.stammdatenService.loadHaendler();
        const reklFreigabe$ = this.stammdatenService.getTierreklamationen(true);

        forkJoin({
            abteileK: abteileKunde$,
            abteileG: abteileGruppe$,
            herkunft: herkunft$,
            haendler: haendler$,
            reklG: reklGruppe$,
            reklF: reklFreigabe$,
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ abteileK, abteileG, herkunft, haendler, reklG, reklF }) => {
                    this.loadingOptions = false;
                    const abteilLabels = uniqueLabels([
                        ...recordsOf(abteileG).map(r => r.abteil),
                        ...recordsOf(abteileK).map(r => r.abteil),
                    ]);
                    this.abteilOptionen = abteilLabels.length
                        ? abteilLabels
                        : ['Vorne', 'Hinten', 'Mitte'];

                    const herkunftRows = [...recordsOf(herkunft)].sort(
                        (a, b) => Number(b.anzahl ?? 0) - Number(a.anzahl ?? 0),
                    );
                    const herkunftOpts: SammelnHerkunftOption[] = [];
                    for (const row of herkunftRows) {
                        const opt = this.saveService.herkunftOptionFromGroupRow(row);
                        if (opt && !herkunftOpts.some(o => o.label === opt.label)) {
                            herkunftOpts.push(opt);
                        }
                    }
                    this.herkunftOptionen = herkunftOpts;

                    const haendlerOpts: Array<{ label: string; id?: number }> = [];
                    for (const h of recordsOf(haendler)) {
                        const label = String(h.name ?? '').trim();
                        const id = Number(h.viehhaendler_id ?? 0);
                        if (!label) continue;
                        haendlerOpts.push({ label, id: id > 0 ? id : undefined });
                    }
                    this.haendlerOptionen = haendlerOpts.length
                        ? haendlerOpts
                        : ['Venneker', 'Brinkmann', 'Sonstige'].map(label => ({ label }));

                    const reklByLabel = new Map<string, number>();
                    const reklOrdered: Array<{ label: string; id?: number }> = [];
                    const pushRekl = (label: string, id: number) => {
                        if (!label) return;
                        if (!reklByLabel.has(label) && id > 0) reklByLabel.set(label, id);
                        if (!reklOrdered.some(o => o.label === label)) {
                            reklOrdered.push({ label, id: id > 0 ? id : undefined });
                        }
                    };
                    const groupRekl = [...recordsOf(reklG)].sort(
                        (a, b) => Number(b.anzahl ?? 0) - Number(a.anzahl ?? 0),
                    );
                    for (const r of groupRekl) {
                        pushRekl(
                            String(r.reklamation ?? '').trim(),
                            Number(r.tierreklamation_id ?? 0),
                        );
                    }
                    for (const r of recordsOf(reklF)) {
                        pushRekl(
                            String(r.reklamation ?? r.bezeichnung ?? '').trim(),
                            Number(r.tierreklamation_id ?? 0),
                        );
                    }
                    this.reklamationOptionen = reklOrdered;
                },
                error: () => {
                    this.loadingOptions = false;
                },
            });
    }
}
